import { BookOpenTextIcon, CalendarCheckIcon, CalendarXIcon, ClipboardCheckIcon, CloudRainIcon, FolderSearchIcon, LayoutGridIcon, PencilRulerIcon, ShapesIcon, TrophyIcon, UsersIcon } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

// EDUFIS PRONTO™ (Italia). Promessa = consegna: ogni numero qui corrisponde ai 6 volumi reali.
export const PRODUCT_NAME = 'EDUFIS PRONTO™';
export const heroGuarantees = ['Download digitale immediato', 'Pagamento unico', 'Nessun abbonamento', 'Garanzia di 7 giorni'];
export const stats = [{
  value: '6',
  label: 'Volumi digitali in PDF'
}, {
  value: '110+',
  label: 'Lezioni, sessioni e giochi pronti'
}, {
  value: '120+',
  label: 'Schede visuali di tecnica ed errori'
}, {
  value: '8',
  label: 'UDA pronte, da settembre a giugno'
}, {
  value: '5',
  label: 'Bonus inclusi'
}];
export const differenceStats = [{
  value: 'Primaria e Secondaria',
  label: 'Programmazione annuale per Primaria, Secondaria di I e di II grado'
}, {
  value: '6 volumi digitali',
  label: 'Programmazione, sport di squadra, atletica e ginnastica, giochi, valutazione, planner'
}, {
  value: 'PDF stampabili',
  label: 'Da usare sul tablet in palestra o da stampare'
}];
export const beforeItems: Array<{
  title: string;
  text: string;
}> = [{
  title: 'La domenica sera sparisce',
  text: 'Ore davanti al computer a cercare esercizi e giochi per le lezioni della settimana – e non hai ancora finito.'
}, {
  title: 'Attività sparse, nessun percorso',
  text: 'Un gioco da un video, un esercizio da un sito: ogni lezione è un pezzo isolato, senza una progressione.'
}, {
  title: '25–30 alunni e un solo pallone per due',
  text: 'Organizzare gruppi, spazi e materiale perché nessuno resti fermo richiede tempo ogni volta.'
}, {
  title: 'Piove e la palestra è occupata',
  text: 'Quando salta la palestra, la lezione diventa improvvisazione all\'ultimo minuto.'
}, {
  title: 'Programmazione e UDA da scrivere',
  text: 'Documenti, obiettivi e traguardi da riscrivere ogni anno, oltre alle lezioni.'
}, {
  title: 'Valutare senza criteri chiari',
  text: 'Osservare 30 alunni in movimento e trasformarlo in un giudizio è difficile senza griglie pronte.'
}];
export const afterItems = ['Programmazione annuale per Primaria, Secondaria di I e II grado', '8 UDA pronte, da settembre a giugno', '16 lezioni da 60\' di calcio, pallacanestro, pallavolo e pallamano', '50 sessioni di atletica, ginnastica, fitness e capacità motorie', '50 giochi, staffette e sfide senza eliminazione', 'Piano B per la pioggia e la palestra occupata', '120+ schede visuali con errori e correzioni', 'Rubriche, griglie e prove d\'ingresso pronte', 'Planner annuale di 36 settimane'];
export const recognizeItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: CalendarXIcon,
  title: 'Il weekend va via a preparare lezioni',
  text: 'Passi parte del fine settimana a cercare esercizi e giochi per la settimana successiva?'
}, {
  icon: FolderSearchIcon,
  title: 'Non sai quale attività usare',
  text: 'Cerchi continuamente giochi adatti all\'età, al numero di alunni e al materiale che hai?'
}, {
  icon: UsersIcon,
  title: 'Classi numerose, qualcuno resta fermo',
  text: 'Vorresti lezioni organizzate a gruppi e stazioni, dove tutti si muovono?'
}, {
  icon: CloudRainIcon,
  title: 'Il giorno di pioggia ti prende alla sprovvista',
  text: 'Ti servono attività pronte per l\'aula o il corridoio quando la palestra non c\'è?'
}, {
  icon: PencilRulerIcon,
  title: 'Programmazione e UDA da rifare',
  text: 'Vorresti programmazione, UDA e griglie di valutazione già pronte?'
}, {
  icon: LayoutGridIcon,
  title: 'Ti serve una struttura per tutto l\'anno',
  text: 'Vorresti tutto il materiale in un unico sistema, per trovare subito la lezione giusta?'
}];

export const volumes: Array<{
  number: string;
  level: string;
  classes: string;
  title: string;
  tagline: string;
  intro: string;
  items: string[];
  outro: string;
  note?: string;
  spine: string;
  rule: string;
}> = [{
  number: 'Volume 01',
  level: 'Programma',
  classes: 'Primaria · Secondaria I e II grado',
  title: 'Programmazione Pronta + Sport di Squadra',
  tagline: 'Il cuore del sistema: l\'anno organizzato e le lezioni di squadra pronte.',
  intro: 'Il primo volume contiene la programmazione annuale per ogni livello, 8 UDA pronte e 16 lezioni da 60 minuti di calcio, pallacanestro, pallavolo e pallamano.',
  items: ['I 4 nuclei e i traguardi', 'Programmazione annuale Primaria 1ª–2ª e 3ª–5ª', 'Programmazione Secondaria I e II grado', 'Piano di 36 settimane', '8 UDA pronte', '16 lezioni da 60\' (4 sport di squadra)', 'Piano B: pioggia e palestra occupata', 'Prove d\'ingresso e griglie di valutazione'],
  outro: 'Apri la programmazione, prendi l\'UDA del mese e la lezione della settimana.',
  spine: 'bg-blue-700 text-white',
  rule: 'border-white/30'
}, {
  number: 'Volume 02',
  level: 'Atletica',
  classes: 'Primaria e Secondaria',
  title: 'Atletica, Ginnastica, Fitness e Capacità Motorie',
  tagline: '50 sessioni visive per le discipline individuali.',
  intro: 'Il secondo volume raccoglie 50 sessioni pronte: atletica, ginnastica, fitness e capacità motorie, con la stessa struttura della lezione da 60 minuti.',
  items: ['Atletica: corsa, salti, lanci, staffette', 'Ginnastica: equilibrio, capovolte, sequenze', 'Fitness: circuiti a stazioni', 'Capacità motorie: coordinazione, ritmo, lateralità', 'Guida alla differenziazione su 3 livelli', 'Griglia di osservazione 1–4'],
  outro: 'Ogni sessione con tempi, materiale, consegne e varianti per età.',
  spine: 'bg-green-600 text-white',
  rule: 'border-white/30'
}, {
  number: 'Volume 03',
  level: 'Giochi',
  classes: 'Primaria e Secondaria',
  title: 'Giochi, Staffette e Sfide',
  tagline: '50 giochi dove nessuno viene eliminato.',
  intro: 'Il terzo volume contiene 50 giochi visivi: attivazione e reazione, cooperazione, staffette e circuiti, giochi inclusivi.',
  items: ['Giochi di reazione e attivazione', 'Giochi cooperativi', 'Staffette e circuiti', 'Giochi inclusivi', '12 giochi senza materiale', 'Scheda di osservazione stampabile'],
  outro: 'Il gioco giusto per iniziare, per le classi numerose o per l\'ultima ora.',
  spine: 'bg-yellow-400 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Volume 04',
  level: 'Valutazione',
  classes: 'Primaria e Secondaria',
  title: 'Valutazione: Schede, Rubriche e Griglie',
  tagline: 'Osservare e valutare 30 alunni senza perdere ore.',
  intro: 'Il quarto volume raccoglie 50 schede, rubriche e strumenti visivi per osservare, valutare e monitorare i progressi.',
  items: ['Scala a 4 livelli', 'Griglia per 30 alunni in una pagina', 'Rubriche rapide per sport e area', 'Scheda per comunicare i progressi', 'Riepilogo di periodo', 'Valutazione a rotazione'],
  outro: 'Criteri chiari, coerenti con quello che hai fatto in palestra.',
  spine: 'bg-red-600 text-white',
  rule: 'border-white/30'
}, {
  number: 'Volume 05',
  level: 'Tecnica',
  classes: 'Primaria e Secondaria',
  title: 'Schede Visuali di Tecnica ed Errori',
  tagline: '120+ schede per mostrare, correggere e far riprovare.',
  intro: 'Il quinto volume contiene oltre 120 schede visuali di tecnica: sport di squadra, atletica, ginnastica, fitness e capacità motorie, con gli errori più comuni e la correzione.',
  items: ['Sport di squadra', 'Atletica e ginnastica', 'Fitness e capacità motorie', 'Errori e correzioni (così no / così sì)', 'Mini schede da ritagliare'],
  outro: 'Mostra la scheda, indica 2 punti chiave, fai provare.',
  spine: 'bg-violet-600 text-white',
  rule: 'border-white/30'
}, {
  number: 'Volume 06',
  level: 'Planner',
  classes: 'Primaria e Secondaria',
  title: 'Il Planner Annuale',
  tagline: 'L\'anno in 36 settimane, su carta o su tablet.',
  intro: 'Il sesto volume è il planner visuale dell\'anno: mappa delle 36 settimane, una pagina per ogni mese e modelli pronti da compilare.',
  items: ['Mappa delle 36 settimane', 'Pagina per ogni mese, da settembre a giugno', 'Orario e inventario del materiale', 'Modelli di lezione da 30, 45 e 60\'', 'Scheda per il supplente', 'Calendario delle verifiche'],
  outro: 'Tutto il tuo anno sotto controllo in un unico posto.',
  note: 'Materiale didattico indipendente: non è una pubblicazione ufficiale del Ministero dell\'Istruzione e del Merito.',
  spine: 'bg-orange-500 text-slate-900',
  rule: 'border-slate-900/20'
}];
export const volumesIntro = {
  title: '6 volumi digitali per Primaria e Secondaria',
  text: 'Un sistema organizzato con programmazione, lezioni pronte, giochi, schede visuali e strumenti di valutazione. Apri il volume, scegli la lezione ed entra in palestra. Meno preparazione. Più tempo in movimento.'
};
export const packItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: BookOpenTextIcon,
  title: 'Lezioni pronte',
  text: 'Ogni lezione con tempi, gruppi, materiale e consegne da dire: niente più pagina bianca.'
}, {
  icon: TrophyIcon,
  title: 'Sport di squadra',
  text: 'Calcio, pallacanestro, pallavolo e pallamano con progressioni dalla Primaria alla Secondaria.'
}, {
  icon: ShapesIcon,
  title: 'Schede visuali',
  text: 'Campi disegnati, posizioni corrette ed errori comuni: gli alunni capiscono guardando.'
}, {
  icon: CloudRainIcon,
  title: 'Piano B pronto',
  text: 'Attività per l\'aula e lezioni teoriche per i giorni di pioggia o con la palestra occupata.'
}, {
  icon: ClipboardCheckIcon,
  title: 'Valutazione',
  text: 'Prove d\'ingresso, rubriche a 4 livelli e griglie per 30 alunni.'
}, {
  icon: CalendarCheckIcon,
  title: 'Programmazione annuale',
  text: 'Per Primaria, Secondaria di I e di II grado, sui 4 nuclei delle Indicazioni Nazionali.'
}];
export const pageTypes = ['Programmazione', 'Lezioni pronte', 'Schede visuali', 'Valutazione'];
export const bonuses: Array<{
  title: string;
  text: string;
  items: string[];
  benefit: string;
}> = [{
  title: 'Registro Elettronico Express',
  text: 'Frasi pronte da copiare nel registro elettronico.',
  items: ['Frasi per la Primaria', 'Frasi per la Secondaria', 'Pioggia, inclusione, classi difficili', 'Commenti di fine periodo'],
  benefit: 'Il registro compilato in pochi minuti.'
}, {
  title: 'Palestra Senza Caos',
  text: 'Routine e segnali per gestire classi numerose e rumorose in sicurezza.',
  items: ['Rituale di ingresso', '5 regole d\'oro', 'Segnali di stop e ascolto', '4 cartelli da stampare'],
  benefit: 'Ordine e sicurezza dal primo minuto.'
}, {
  title: 'Supplenza Pronta in 10 Minuti',
  text: 'Il pacchetto da lasciare al supplente quando non ci sei.',
  items: ['Nota pronta per il supplente', 'Checklist di sicurezza', 'Attività senza materiale', 'Circuiti con i tempi'],
  benefit: 'Assenza imprevista? La lezione è già pronta.'
}, {
  title: 'Comunicazioni Pronte per Genitori e Dirigenza',
  text: 'Modelli di comunicazione per famiglie, dirigenza e colleghi.',
  items: ['Lettera di inizio anno', 'Materiale e sicurezza', 'Uscite ed eventi sportivi', 'Traccia per la riunione iniziale'],
  benefit: 'Basta compilare e inviare.'
}, {
  title: 'Setup Docente 5 Minuti',
  text: 'Come organizzare tutti i PDF per trovare ogni lezione in pochi secondi.',
  items: ['Cartelle digitali', 'Routine settimanale di 5 minuti', 'Piano di avvio in 7 giorni', 'Modalità "ho lezione tra 10 minuti"'],
  benefit: 'Tutto il materiale in ordine, sempre.'
}];
export const BONUS_VALUE = '19 €';
export const steps: Array<{
  title: string;
  text: string;
}> = [{
  title: 'Apri EDUFIS PRONTO™',
  text: 'Scarica i 6 volumi e apri la programmazione della tua classe nel Volume 01.'
}, {
  title: 'Scegli l\'UDA e la lezione',
  text: 'Prendi l\'UDA del mese e la lezione della settimana dai volumi 01, 02 o 03.'
}, {
  title: 'Preparati in 5 minuti',
  text: 'Leggi tempi, gruppi e materiale e prepara le schede visuali che ti servono.'
}, {
  title: 'Entra in palestra',
  text: 'Fai lezione e valuta con le griglie del Volume 04.'
}];
export const audienceItems = ['Insegni Educazione Fisica nella Primaria', 'Insegni Scienze Motorie nella Secondaria di I o II grado', 'Vuoi smettere di preparare ogni lezione da zero', 'Hai classi numerose e vuoi che nessuno resti fermo', 'Ti servono giochi e attività per tutte le età', 'Vuoi un piano B per la pioggia e la palestra occupata', 'Vuoi programmazione, UDA e griglie già pronte', 'Vuoi entrare in palestra più preparato'];
export const timeValueTasks = ['Cercare giochi ed esercizi adatti', 'Organizzare gruppi, spazi e materiale', 'Scrivere programmazione e UDA', 'Inventare il piano B quando piove', 'Preparare griglie e giudizi'];
export const offerRows: Array<{
  label: string;
  detail: string;
}> = [{
  label: '6 volumi digitali in PDF',
  detail: 'Programmazione e sport di squadra, atletica e ginnastica, giochi, valutazione, schede visuali, planner'
}, {
  label: '5 bonus senza costi aggiuntivi',
  detail: 'Registro elettronico, palestra senza caos, supplenza pronta, comunicazioni, setup docente'
}, {
  label: 'Primaria, Secondaria di I e II grado',
  detail: 'Lezioni pronte, giochi, schede visuali, griglie di valutazione'
}];
export const offerIncludes = ['Download digitale immediato', 'File PDF da salvare', 'Schede da stampare', 'Nessun prodotto fisico spedito'];
export const PRICE = '19 €';
export const PRICE_OLD = '67 €';

// Solo recensioni autentiche di EDUFIS PRONTO. La sezione resta nascosta finché è vuota.
export const testimonials: Array<{
  quote: string;
  name: string;
  location: string;
}> = [];
export const faqs = [{
  q: 'Come ricevo il materiale?',
  a: 'EDUFIS PRONTO™ è un prodotto digitale. Dopo l\'acquisto ricevi via e-mail l\'accesso ai 6 volumi e ai 5 bonus in PDF, da scaricare e salvare. Puoi usarli su computer, tablet o smartphone e stampare quello che ti serve.'
}, {
  q: 'È adatto alla scuola italiana?',
  a: 'Sì. La programmazione è organizzata sui 4 nuclei delle Indicazioni Nazionali per l\'Educazione Fisica. È un materiale didattico indipendente, non una pubblicazione ufficiale del Ministero.'
}, {
  q: 'Per quali classi è?',
  a: 'Per la Primaria e la Secondaria di I e II grado. Ogni lezione e gioco ha varianti per età; il Volume 01 contiene la programmazione annuale per ogni livello.'
}, {
  q: 'Funziona con classi numerose?',
  a: 'Sì. Le lezioni sono pensate per 20–30 alunni, a gruppi e stazioni, con la regola "nessuno resta fermo, nessuno viene eliminato".'
}, {
  q: 'E se piove o la palestra è occupata?',
  a: 'Il Volume 01 ha un piano B con attività per l\'aula e lezioni teoriche pronte; il Volume 03 ha 12 giochi senza materiale.'
}, {
  q: 'Posso davvero preparare una lezione in 5 minuti?',
  a: 'EDUFIS PRONTO™ è pensato per rendere la preparazione più veloce: apri il volume, scegli la lezione e la rivedi in circa cinque minuti. Il tempo può variare in base alla classe e agli adattamenti necessari.'
}, {
  q: 'I 5 bonus sono inclusi?',
  a: `Sì. Tutti e cinque i bonus sono inclusi senza costi aggiuntivi – tutto a ${PRICE}.`
}, {
  q: 'È un abbonamento?',
  a: 'No. Paghi una sola volta e ricevi l\'accesso al materiale digitale. Nessun costo ricorrente.'
}, {
  q: 'C\'è una garanzia?',
  a: 'Sì. EDUFIS PRONTO™ ha una garanzia di soddisfazione di 7 giorni. Se il materiale non soddisfa le tue aspettative, puoi contattarci e chiedere il rimborso entro il periodo di garanzia, secondo le condizioni.'
}];
